import { useState, useEffect, useCallback } from 'react';
import {
  KeyRound, Plus, Copy, Check, ShieldCheck, AlertTriangle, X,
  Ban, Clock, Info, RefreshCw, Link2,
} from 'lucide-react';

import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { Spinner }       from '@/components/ui/Spinner';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { cn }            from '@/utils/cn';
import {
  integrationClientApi,
  INTEGRATION_CLIENT_PERMISSIONS,
  type ApiClientDto,
  type CreatedApiClientDto,
} from '@/api/integrationClient.api';

// ── Status derivation ─────────────────────────────────────────────────────────
type KeyStatus = 'Active' | 'Revoked' | 'Expired';

const statusOf = (c: ApiClientDto): KeyStatus => {
  if (c.revokedAt || !c.isActive) return 'Revoked';
  if (c.expiresAt && new Date(c.expiresAt).getTime() <= Date.now()) return 'Expired';
  return 'Active';
};

const STATUS_STYLE: Record<KeyStatus, string> = {
  Active:  'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
  Revoked: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  Expired: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

const fmtDate = (v?: string | null) =>
  v ? new Date(v).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const ALL_CODES = INTEGRATION_CLIENT_PERMISSIONS.map(p => p.code);

// ── Page ────────────────────────────────────────────────────────────────────────
export const IntegrationClientsPage = () => {
  const { hasPermission } = usePermission();
  const canManage = hasPermission(Permissions.INTEGRATION_CLIENT_MANAGE);

  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [clients, setClients] = useState<ApiClientDto[]>([]);

  // create wizard
  const [showCreate, setShowCreate] = useState(false);
  const [created, setCreated]       = useState<CreatedApiClientDto | null>(null);

  // revoke
  const [revokeTarget, setRevokeTarget] = useState<ApiClientDto | null>(null);
  const [revoking, setRevoking]         = useState(false);

  // unlink (clear the myPOS company link)
  const [unlinkTarget, setUnlinkTarget] = useState<ApiClientDto | null>(null);
  const [unlinking, setUnlinking]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setClients(await integrationClientApi.list());
    } catch (err) {
      console.error('Failed to load API keys', err);
      setError('Could not load API keys. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await integrationClientApi.revoke(revokeTarget.id);
      setRevokeTarget(null);
      await load();
    } catch (err) {
      console.error('Failed to revoke API key', err);
      setError('Could not revoke the key. Please try again.');
    } finally {
      setRevoking(false);
    }
  };

  const doUnlink = async () => {
    if (!unlinkTarget) return;
    setUnlinking(true);
    try {
      await integrationClientApi.unlink(unlinkTarget.id);
      setUnlinkTarget(null);
      await load();
    } catch (err) {
      console.error('Failed to unlink API key', err);
      setError('Could not unlink the key. Please try again.');
    } finally {
      setUnlinking(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <KeyRound size={20} className="text-signal" />
            API Keys
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Service-account keys that let external systems (e.g. myPOS ONE) post into this company.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading}
            className="p-2 rounded-lg text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
            title="Refresh">
            <RefreshCw size={16} className={cn(loading && 'animate-spin')} />
          </button>
          {canManage && (
            <button onClick={() => { setCreated(null); setShowCreate(true); }}
              className="px-3.5 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg transition-colors flex items-center gap-1.5">
              <Plus size={15} /> Create API key
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-5xl space-y-4">
          {/* Explainer */}
          <div className="flex items-start gap-2.5 rounded-xl border border-signal/20 bg-signal/5 px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
            <Info size={16} className="text-signal flex-shrink-0 mt-0.5" />
            <p>
              A key is shown <strong>once, at creation</strong>: copy it immediately. In myPOS ONE, open the
              company that keeps its books here, go to <em>Integrations › myLedger ONE Integration</em>, paste
              the key, press Check key, then Connect. One key links one myPOS ONE company to this company.
              myLedger stores only a hash, never the key itself, and service keys may only hold
              integration-posting permissions. Revoke a key to cut a system off instantly.
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 dark:border-rose-900/40 dark:bg-rose-900/20 px-4 py-2.5 text-sm text-rose-700 dark:text-rose-300">
              <AlertTriangle size={15} /> {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20"><Spinner size="lg" /></div>
          ) : clients.length === 0 ? (
            <div className="text-center py-16 rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
              <KeyRound size={32} className="mx-auto text-slate-300 dark:text-slate-600" />
              <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">No API keys yet</p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                Create a key to connect myPOS ONE (or another system) to this company.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-card">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Source system</th>
                    <th className="px-4 py-3 font-semibold">Key</th>
                    <th className="px-4 py-3 font-semibold">Linked myPOS company</th>
                    <th className="px-4 py-3 font-semibold">Perms</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Last used</th>
                    <th className="px-4 py-3 font-semibold">Created</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {clients.map(c => {
                    const st = statusOf(c);
                    return (
                      <tr key={c.id} className="border-b border-slate-100 dark:border-slate-700/50 last:border-0">
                        <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{c.name}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{c.sourceSystem}</td>
                        <td className="px-4 py-3">
                          <code className="text-xs font-mono text-slate-500 dark:text-slate-400">{c.keyPrefix}…</code>
                        </td>
                        <td className="px-4 py-3">
                          {c.bound ? (
                            <span className="inline-flex items-center gap-1.5 text-slate-700 dark:text-slate-200"
                              title={c.bound.boundAt ? `Linked ${fmtDate(c.bound.boundAt)}` : undefined}>
                              <Link2 size={13} className="text-signal flex-shrink-0" />
                              {c.bound.sourceCompanyCode && (
                                <span className="font-medium">{c.bound.sourceCompanyCode}</span>
                              )}
                              <span className="text-slate-500 dark:text-slate-400">
                                {c.bound.sourceCompanyName || (!c.bound.sourceCompanyCode ? 'Linked' : '')}
                              </span>
                            </span>
                          ) : (
                            <span className="text-slate-400 dark:text-slate-500">Not linked</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300" title={c.permissions.join(', ')}>
                          {c.permissions.length}
                        </td>
                        <td className="px-4 py-3">
                          <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold', STATUS_STYLE[st])}>
                            {st === 'Active' && <ShieldCheck size={12} />}
                            {st === 'Revoked' && <Ban size={12} />}
                            {st === 'Expired' && <Clock size={12} />}
                            {st}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-500 dark:text-slate-400">{fmtDate(c.lastUsedAt)}</td>
                        <td className="px-4 py-3 text-slate-500 dark:text-slate-400">{fmtDate(c.createdDate)}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {canManage && c.bound && (
                            <button onClick={() => setUnlinkTarget(c)}
                              className="mr-3 text-xs font-semibold text-slate-600 hover:text-slate-800 dark:text-slate-300 dark:hover:text-white hover:underline">
                              Unlink
                            </button>
                          )}
                          {canManage && st === 'Active' && (
                            <button onClick={() => setRevokeTarget(c)}
                              className="text-xs font-semibold text-rose-600 hover:text-rose-700 dark:text-rose-400 hover:underline">
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create / reveal modal */}
      {showCreate && (
        <CreateKeyModal
          created={created}
          onCreated={setCreated}
          onClose={() => { setShowCreate(false); if (created) load(); }}
        />
      )}

      {/* Revoke confirm */}
      <ConfirmDialog
        isOpen={!!revokeTarget}
        variant="delete"
        title="Revoke this API key?"
        confirmLabel="Yes, revoke"
        loading={revoking}
        message={
          <span>
            Any system using <strong>{revokeTarget?.name}</strong> will stop working immediately.
            This cannot be undone — you’d have to issue a new key. Continue?
          </span>
        }
        onConfirm={doRevoke}
        onCancel={() => setRevokeTarget(null)}
      />

      {/* Unlink confirm */}
      <ConfirmDialog
        isOpen={!!unlinkTarget}
        variant="warning"
        title="Unlink this key from its myPOS company?"
        confirmLabel="Yes, unlink"
        loading={unlinking}
        message={
          <span>
            <strong>{unlinkTarget?.name}</strong> is linked to myPOS company{' '}
            <strong>
              {[unlinkTarget?.bound?.sourceCompanyCode, unlinkTarget?.bound?.sourceCompanyName].filter(Boolean).join(' ') || 'unnamed'}
            </strong>.
            After unlinking, the key can be linked to a different myPOS company the next time it connects.
            Continue?
          </span>
        }
        onConfirm={doUnlink}
        onCancel={() => setUnlinkTarget(null)}
      />
    </div>
  );
};

// ── Create / reveal modal ───────────────────────────────────────────────────────
interface CreateKeyModalProps {
  created: CreatedApiClientDto | null;
  onCreated: (c: CreatedApiClientDto) => void;
  onClose: () => void;
}

const CreateKeyModal = ({ created, onCreated, onClose }: CreateKeyModalProps) => {
  const [name, setName]             = useState('');
  const [sourceSystem, setSource]   = useState('myPOS');
  const [perms, setPerms]           = useState<string[]>(ALL_CODES);
  const [expiresAt, setExpiresAt]   = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError]   = useState<string | null>(null);
  const [copied, setCopied]         = useState(false);

  const toggle = (code: string) =>
    setPerms(p => p.includes(code) ? p.filter(x => x !== code) : [...p, code]);

  const submit = async () => {
    setFormError(null);
    if (!name.trim())         { setFormError('Give the key a name so you can recognise it later.'); return; }
    if (!sourceSystem.trim()) { setFormError('Source system is required.'); return; }
    if (perms.length === 0)   { setFormError('Select at least one permission.'); return; }
    setSubmitting(true);
    try {
      const result = await integrationClientApi.create({
        name: name.trim(),
        sourceSystem: sourceSystem.trim(),
        permissions: perms,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      onCreated(result);
    } catch (err) {
      console.error('Failed to create API key', err);
      setFormError('Could not create the key. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setFormError('Copy failed — select the key and copy it manually.');
    }
  };

  return (
    <div
      role="dialog" aria-modal="true"
      className="fixed inset-0 z-[500] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onMouseDown={e => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-auto animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-5 py-3.5">
          <h2 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <KeyRound size={17} className="text-signal" />
            {created ? 'API key created' : 'Create API key'}
          </h2>
          <button onClick={onClose} disabled={submitting}
            className="p-1 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">
            <X size={16} />
          </button>
        </div>

        {created ? (
          /* ── One-time reveal ── */
          <div className="p-5 space-y-4">
            <div className="flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <p>Copy this key now. For security it is <strong>never shown again</strong> — if you lose it, revoke this key and create a new one.</p>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">Your API key</label>
              <div className="flex items-stretch gap-2">
                <code className="flex-1 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 px-3 py-2.5 text-xs font-mono text-slate-800 dark:text-slate-100 break-all select-all">
                  {created.apiKey}
                </code>
                <button onClick={copy}
                  className={cn('flex-shrink-0 px-3 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors',
                    copied ? 'bg-teal-600 text-white' : 'bg-signal text-white hover:bg-signal-light')}>
                  {copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy</>}
                </button>
              </div>
            </div>

            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div><dt className="text-xs text-slate-400">Name</dt><dd className="text-slate-800 dark:text-slate-100 font-medium">{created.name}</dd></div>
              <div><dt className="text-xs text-slate-400">Source system</dt><dd className="text-slate-800 dark:text-slate-100 font-medium">{created.sourceSystem}</dd></div>
              <div><dt className="text-xs text-slate-400">Prefix</dt><dd className="font-mono text-slate-600 dark:text-slate-300">{created.keyPrefix}</dd></div>
              <div><dt className="text-xs text-slate-400">Expires</dt><dd className="text-slate-600 dark:text-slate-300">{fmtDate(created.expiresAt)}</dd></div>
            </dl>

            <div className="flex justify-end pt-1">
              <button onClick={onClose}
                className="px-4 py-2 text-sm font-semibold text-white bg-slate-700 hover:bg-slate-800 dark:bg-slate-600 dark:hover:bg-slate-500 rounded-lg">
                Done
              </button>
            </div>
          </div>
        ) : (
          /* ── Create form ── */
          <div className="p-5 space-y-4">
            {formError && (
              <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 dark:border-rose-900/40 dark:bg-rose-900/20 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
                <AlertTriangle size={14} /> {formError}
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">Name</label>
              <input value={name} onChange={e => setName(e.target.value)} autoFocus
                placeholder="e.g. myPOS ONE — Production"
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:border-signal focus:ring-1 focus:ring-signal outline-none" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">Source system</label>
                <input value={sourceSystem} onChange={e => setSource(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:border-signal focus:ring-1 focus:ring-signal outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">Expires <span className="normal-case font-normal text-slate-400">(optional)</span></label>
                <input type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:border-signal focus:ring-1 focus:ring-signal outline-none" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Permissions</label>
                <button type="button"
                  onClick={() => setPerms(perms.length === ALL_CODES.length ? [] : ALL_CODES)}
                  className="text-xs font-medium text-signal hover:underline">
                  {perms.length === ALL_CODES.length ? 'Clear all' : 'Select all'}
                </button>
              </div>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700/50">
                {INTEGRATION_CLIENT_PERMISSIONS.map(p => (
                  <label key={p.code} className="flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40">
                    <input type="checkbox" checked={perms.includes(p.code)} onChange={() => toggle(p.code)}
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-gray-600 text-signal focus:ring-signal" />
                    <span>
                      <span className="block text-sm font-medium text-slate-800 dark:text-slate-100">{p.label}</span>
                      <span className="block text-xs text-slate-400 dark:text-slate-500">{p.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1">
                <ShieldCheck size={12} /> Service accounts can only ever hold these integration permissions — never administrative ones.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} disabled={submitting}
                className="px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-lg disabled:opacity-50">
                Cancel
              </button>
              <button onClick={submit} disabled={submitting}
                className="px-4 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg disabled:opacity-50 flex items-center gap-1.5">
                {submitting ? <><Spinner size="sm" /> Creating…</> : <><KeyRound size={15} /> Create key</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
